import { Router } from '@angular/router';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce, tap } from 'rxjs/operators';
import { MessageService } from '../../../../services/message/message.service';
import { DeliverySummary } from '../../../../services/message/delivery.service';
import { IMessageReportContacts } from '../../campaigns/types/messagesTypes';
import { CsvColumn } from '../../../../../utils/csv';
import { formatWhen } from '../../../../../utils/sms';

/**
 * Shared by the message details page and the delivery report: what the
 * receipt states mean, how to read a receipt row, and how to collect every
 * row of a send for a CSV export or a resend.
 */

/** A receipt row as the API returns it. `blackListed` is on the DTO but not in the shared type. */
export interface ReportRow extends IMessageReportContacts {
  blackListed?: boolean | null;
  createdAt?: string | null;
}

/** Receipt states that mean the SMS will not arrive (matches DeliveryService). */
export const FAILED_STATES = ['REJECTED', 'UNDELIVERABLE', 'EXPIRED', 'DELETED', 'SKIPPED'];
/** States that may still turn into a delivery. */
export const IN_FLIGHT_STATES = ['QUEUED', 'PENDING', 'SCHEDULED', 'EN_ROUTE', 'ACCEPTED', 'UNKNOWN'];

export type DeliveryFilter = 'all' | 'delivered' | 'failed' | 'inflight';

export function isDeliveryFilter(value: unknown): value is DeliveryFilter {
  return value === 'all' || value === 'delivered' || value === 'failed' || value === 'inflight';
}

/** Query criteria for one delivery filter. */
export function deliveryCriteria(filter: DeliveryFilter): Record<string, string[]> {
  switch (filter) {
    case 'delivered':
      return { 'deliveryStatus.in': ['DELIVERED'] };
    case 'failed':
      return { 'deliveryStatus.in': FAILED_STATES };
    case 'inflight':
      return { 'deliveryStatus.in': IN_FLIGHT_STATES };
    default:
      return {};
  }
}

/** How many receipts a filter should hold, from the delivery summary. */
export function summaryCount(summary: DeliverySummary | null, filter: DeliveryFilter): number | null {
  if (!summary) return null;
  switch (filter) {
    case 'delivered':
      return summary.delivered;
    case 'failed':
      return summary.failed;
    case 'inflight':
      return summary.inFlight;
    default:
      return summary.total;
  }
}

/** SMS validity: after this a missing receipt will not arrive. */
const RECEIPT_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Still sending, or recent enough that missing receipts may yet arrive. */
export function receiptsDue(status: string | null | undefined, sentAt: Date | null): boolean {
  if (/^active$/i.test(status ?? '')) return true;
  if (!sentAt) return true;
  return Date.now() - sentAt.getTime() < RECEIPT_WINDOW_MS;
}

/**
 * Label and app-status keyword for a receipt state. app-status picks its dot
 * from the keyword, so "UNDELIVERABLE" (which contains "deliver") is passed
 * as "failed" rather than as itself.
 */
export function receiptStatus(raw: string | null | undefined): { label: string; tone: string } {
  const state = String(raw ?? '').trim().toUpperCase();
  if (!state) return { label: 'No receipt', tone: 'inactive' };
  // "Undeliverable" reads as plain "Failed" everywhere in the console.
  const label =
    state === 'UNDELIVERABLE' ? 'Failed' : state.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());
  if (state === 'DELIVERED') return { label, tone: 'delivered' };
  if (FAILED_STATES.includes(state)) return { label, tone: 'failed' };
  // The waiting states say where the message is, so "accepted" never reads
  // as delivered and "queued" only means not sent yet.
  if (state === 'QUEUED' || state === 'PENDING' || state === 'SCHEDULED') return { label: 'Queued to send', tone: 'pending' };
  if (state === 'ACCEPTED') return { label: 'Accepted by network', tone: 'sending' };
  if (state === 'EN_ROUTE') return { label: 'On its way', tone: 'sending' };
  if (state === 'UNKNOWN') return { label: 'No status from network', tone: 'inactive' };
  return { label, tone: 'pending' };
}

/** One receipt, ready for the table. */
export interface ReceiptView {
  key: string | number;
  name: string;
  number: string;
  group: string;
  gender: string;
  age: string;
  country: string;
  statusLabel: string;
  statusTone: string;
  failed: boolean;
  when: string;
  exact: string;
  /** Machine-readable receipt time for <time datetime>. */
  iso: string;
  blacklisted: boolean;
}

const exactFormat = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

export function exactTime(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : exactFormat.format(date);
}

/** ISO 8601 for a <time datetime> attribute; '' when the value is not a date. */
export function isoTime(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
}

export function groupName(group: unknown): string {
  if (!group) return '';
  if (typeof group === 'string') return group;
  if (typeof group === 'object') {
    const named = group as { name?: unknown; groupName?: unknown };
    return String(named.name ?? named.groupName ?? '');
  }
  return String(group);
}

function sentenceCase(value: unknown): string {
  const text = String(value ?? '').trim().toLowerCase();
  return text ? text[0].toUpperCase() + text.slice(1) : '';
}

export function toReceiptViews(rows: unknown): ReceiptView[] {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => !!row && typeof row === 'object')
    .map((row: ReportRow, index): ReceiptView => {
      const status = receiptStatus(row.deliveryStatus);
      const time = row.timeStamp || row.createdAt || null;
      return {
        key: row.id ?? row.uuid ?? `row-${index}`,
        name: String(row.fullName ?? '').trim(),
        number: String(row.mobileNumber ?? '').trim(),
        group: groupName(row.group),
        gender: sentenceCase(row.gender),
        age: row.age === null || row.age === undefined || (row.age as unknown) === '' ? '' : String(row.age),
        country: String(row.country ?? '').trim(),
        statusLabel: status.label,
        statusTone: status.tone,
        failed: status.tone === 'failed',
        when: time ? formatWhen(time) : '—',
        exact: exactTime(time),
        iso: isoTime(time),
        blacklisted: row.blackListed === true,
      };
    });
}

/** Columns of every receipt CSV, so recipients and failures export alike. */
export const RECEIPT_CSV_COLUMNS: CsvColumn<ReportRow>[] = [
  { label: 'Name', value: (row) => row.fullName },
  { label: 'Phone number', value: (row) => row.mobileNumber },
  { label: 'Delivery status', value: (row) => receiptStatus(row.deliveryStatus).label },
  { label: 'Group', value: (row) => groupName(row.group) },
  { label: 'Gender', value: (row) => sentenceCase(row.gender) },
  { label: 'Age', value: (row) => row.age },
  { label: 'Country', value: (row) => row.country },
  { label: 'Receipt time', value: (row) => row.timeStamp },
  {
    label: 'Blacklisted',
    value: (row) => (row.blackListed === true ? 'Yes' : row.blackListed === false ? 'No' : ''),
  },
];

/** Rows fetched per request when collecting a whole send. */
const BULK_PAGE_SIZE = 1000;
/** Most rows one export or resend collects. */
export const COLLECT_CAP = 25_000;

export interface CollectedRows {
  rows: ReportRow[];
  /** Rows matching the criteria, which may be more than were collected. */
  total: number;
  /** False when the API sent no total: `total` is then only what was collected. */
  totalKnown: boolean;
  capped: boolean;
}

/**
 * Every receipt of a send that matches the criteria, one page after another,
 * up to `cap` rows. `onProgress` reports rows collected so far.
 */
export function collectReportRows(
  service: MessageService,
  messageUuid: string,
  criteria: Record<string, unknown>,
  onProgress?: (loaded: number, total: number | null) => void,
  cap = COLLECT_CAP
): Observable<CollectedRows> {
  const fetchPage = (page: number) =>
    service
      .fetchMessageReportPage(messageUuid, {
        ...criteria,
        page,
        size: BULK_PAGE_SIZE,
        sort: 'id,asc',
      })
      .pipe(map((result) => ({ page, ...result })));

  let loaded = 0;
  return fetchPage(0).pipe(
    expand(({ page, rows, total }) => {
      const fetched = (page + 1) * BULK_PAGE_SIZE;
      const more =
        rows.length === BULK_PAGE_SIZE && fetched < cap && (total === null || fetched < total);
      return more ? fetchPage(page + 1) : EMPTY;
    }, 1),
    tap(({ rows, total }) => {
      loaded += rows.length;
      onProgress?.(loaded, total);
    }),
    reduce(
      (acc, { rows, total }) => {
        acc.rows.push(...(rows as ReportRow[]));
        if (total !== null) acc.total = total;
        acc.lastPageFull = rows.length === BULK_PAGE_SIZE;
        return acc;
      },
      { rows: [] as ReportRow[], total: null as number | null, lastPageFull: false }
    ),
    map(({ rows, total, lastPageFull }): CollectedRows => {
      if (total !== null) {
        return { rows: rows.slice(0, cap), total, totalKnown: true, capped: total > cap };
      }
      // No total from the API: a full last page at the cap means more rows may remain.
      const capped = lastPageFull && rows.length >= cap;
      return { rows: rows.slice(0, cap), total: rows.length, totalKnown: false, capped };
    })
  );
}

/** Failed numbers worth retrying: unique, non-empty, never blacklisted. */
export function resendNumbers(rows: ReportRow[]): { numbers: string[]; blacklisted: number } {
  const seen = new Set<string>();
  let blacklisted = 0;
  for (const row of rows) {
    if (row.blackListed === true) {
      blacklisted++;
      continue;
    }
    const number = String(row.mobileNumber ?? '').trim();
    if (number) seen.add(number);
  }
  return { numbers: [...seen], blacklisted };
}

/**
 * Characters of encoded numbers a compose link may carry. Links much longer
 * than ~2,000 characters break when copied, bookmarked or reloaded through
 * the server, so bigger lists travel in the navigation state instead.
 */
export const RESEND_URL_LIMIT = 1800;

export interface ResendTarget {
  numbers: string[];
  subject: string;
  /** True when the numbers are too many for the link and go in navigation state. */
  viaState: boolean;
  /** Blacklisted failures that were left out. */
  blacklisted: number;
  /** True when there were more failures than one resend collects. */
  capped: boolean;
}

export function resendTarget(
  numbers: string[],
  subject: string,
  blacklisted = 0,
  capped = false
): ResendTarget {
  const encoded = encodeURIComponent(numbers.join(','));
  return { numbers, subject, viaState: encoded.length > RESEND_URL_LIMIT, blacklisted, capped };
}

/**
 * Open the composer prefilled with the numbers and subject. Nothing is sent:
 * the composer only fills its fields.
 *   ?numbers=a,b,c&subject=…                 when the list fits in a link
 *   ?subject=… + state { numbers, subject }  when it does not
 */
export function openComposerForResend(router: Router, target: ResendTarget): void {
  const subject = target.subject.trim() || null;
  if (!target.viaState) {
    router.navigate(['/organization/messages/create'], {
      queryParams: { numbers: target.numbers.join(','), subject },
    });
    return;
  }
  router.navigate(['/organization/messages/create'], {
    queryParams: { subject },
    state: { numbers: target.numbers, subject: target.subject, source: 'resend-failed' },
  });
}
